import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { LayoutDashboard, Mail, Star, ChevronLeft, ChevronRight, ChevronDown, LogOut as LogOutIcon, ExternalLink, LayoutGrid } from 'lucide-react';
import { clearWhenconnectSession } from './WhenconnectApp';

const ACCENT = '#f97316';

const formatUsername = (name) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '';

// ─── Styled ───────────────────────────────────────────────────────────────────

const AppShell = styled.div`
  display: flex;
  height: 100vh;
  background: ${p => p.$light ? '#f5f5f7' : '#0a0a0a'};
  overflow: hidden;
`;

const SideNav = styled.nav`
  width: ${p => p.$collapsed ? '64px' : '220px'};
  min-width: ${p => p.$collapsed ? '64px' : '220px'};
  background: ${p => p.$light ? '#ffffff' : '#111111'};
  display: flex;
  flex-direction: column;
  padding: 8px 0 32px;
  position: relative;
  transition: width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1);
  overflow: hidden;
  flex-shrink: 0;
`;

const SideNavLogo = styled.div`
  height: 52px;
  display: flex;
  align-items: center;
  padding: 8px 20px 0;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const LogoText = styled.div`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: nowrap;
  span { color: ${ACCENT}; }
`;

const NavSection = styled.div`padding: 0 10px; margin-bottom: 4px;`;

const NavSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.$light ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)'};
  padding: 0 10px;
  margin: 16px 0 4px;
  white-space: nowrap;
  display: ${p => p.$collapsed ? 'none' : 'block'};
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  color: ${p => p.$active ? '#fff' : (p.$light ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)')};
  text-decoration: none;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: ${p => p.$active ? '600' : '500'};
  background: ${p => p.$active ? ACCENT : 'transparent'};
  transition: all 0.15s ease;

  &:hover {
    color: ${p => p.$active ? '#fff' : (p.$light ? '#111' : '#fff')};
    background: ${p => p.$active ? ACCENT : (p.$light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)')};
  }

  svg { width: 17px; height: 17px; opacity: ${p => p.$active ? 1 : 0.5}; flex-shrink: 0; }
`;

const NavItemLabel = styled.span`
  white-space: nowrap;
  display: ${p => p.$collapsed ? 'none' : 'block'};
`;

const ExternalBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  justify-content: ${p => p.$collapsed ? 'center' : 'flex-start'};
  color: ${p => p.$light ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)'};
  background: transparent;
  border: none;
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.15s ease;
  &:hover { color: ${p => p.$light ? '#111' : '#fff'}; background: ${p => p.$light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'}; }
  svg { width: 17px; height: 17px; opacity: 0.5; flex-shrink: 0; }
`;

const NavDivider = styled.div`
  height: 1px;
  background: ${p => p.$light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)'};
  margin: 10px 14px;
`;

const AppInfo = styled.div`
  margin: auto 14px 0;
  padding: 10px 12px;
  opacity: ${p => p.$collapsed ? 0 : 1};
  transition: opacity 0.15s;
  pointer-events: ${p => p.$collapsed ? 'none' : 'auto'};
`;

const AppInfoName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$light ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.3)'};
`;

const AppInfoMeta = styled.div`
  font-size: 10px;
  color: ${p => p.$light ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.18)'};
  margin-top: 1px;
`;

const CollapseBtn = styled.button`
  position: absolute;
  bottom: 24px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'};
  background: ${p => p.$light ? '#fff' : '#1a1a1a'};
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  z-index: 10;
  &:hover { background: ${p => p.$light ? '#f0f0f0' : '#2a2a2a'}; }
`;

// ─── TopBar ───────────────────────────────────────────────────────────────────

const TopBarLogo = styled.img`
  height: 22px;
  width: auto;
  object-fit: contain;
  display: block;
  filter: ${p => p.$light ? 'brightness(0)' : 'brightness(0) invert(1)'};
  opacity: 0.85;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 24px;
  height: 52px;
  background: ${p => p.$light ? '#ffffff' : '#111111'};
  flex-shrink: 0;
`;

const ProfileBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px 4px 4px;
  border-radius: 999px;
  color: ${p => p.$light ? '#111' : '#fff'};
  transition: background 0.15s;
  &:hover { background: ${p => p.$light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.07)'}; }
`;

const Avatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${ACCENT};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const ProfileName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$light ? '#111' : '#fff'};
`;

const Dropdown = styled.div`
  position: fixed;
  background: ${p => p.$light ? '#fff' : '#1c1c1e'};
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.10)'};
  border-radius: 14px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.18);
  z-index: 10000;
`;

const DropdownHeader = styled.div`
  padding: 8px 12px 10px;
  font-size: 12px;
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.35)'};
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: ${p => p.$light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  margin: 6px 0;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$danger ? '#ef4444' : (p.$light ? '#111' : '#fff')};
  text-align: left;
  transition: background 0.12s;
  &:hover { background: ${p => p.$danger ? 'rgba(239,68,68,0.08)' : (p.$light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)')}; }
  svg { opacity: 0.6; flex-shrink: 0; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: ${p => p.$light ? '#fff' : '#1c1c1e'};
  border-radius: 18px;
  max-width: 380px;
  width: 90%;
  padding: 24px 20px 20px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.10)'};
`;

const ModalTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 700;
  color: ${p => p.$light ? '#111' : '#fff'};
`;

const ModalText = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: ${p => p.$light ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.45)'};
`;

const ModalBtns = styled.div`display: flex; gap: 10px;`;

const ModalBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 20px;
  font-weight: 600;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  background: ${p => p.$danger ? '#ef4444' : (p.$light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.10)')};
  color: ${p => p.$danger ? '#fff' : (p.$light ? '#111' : '#fff')};
  transition: filter 0.15s;
  &:hover { filter: brightness(0.92); }
`;

const MainArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
`;

// ─── Component ────────────────────────────────────────────────────────────────

function getIsLight() {
  const saved = localStorage.getItem('whenconnect_theme');
  if (saved) return saved === 'light';
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}

export default function WhenconnectLayout({ children, onLogout }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('whenconnect_sidebar_collapsed') === 'true');
  const [showProfile, setShowProfile] = useState(false);
  const [profilePos, setProfilePos] = useState({ top: 0, right: 0 });
  const [showLogout, setShowLogout] = useState(false);
  const [showWebsiteConfirm, setShowWebsiteConfirm] = useState(false);
  const [isLight, setIsLight] = useState(getIsLight);
  const profileRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const h = (e) => { if (!localStorage.getItem('whenconnect_theme')) setIsLight(e.matches); };
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfile(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('whenconnect_session') || '{}'); } catch { return {}; } })();
  const username = formatUsername(storedUser.username || storedUser.name) || 'Gebruiker';
  const initials = username.slice(0, 2).toUpperCase();

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('whenconnect_sidebar_collapsed', next ? 'true' : 'false');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <AppShell $light={isLight}>
      {/* Sidebar */}
      <SideNav $collapsed={collapsed} $light={isLight}>
        <NavSection>
          <NavSectionLabel $collapsed={collapsed} $light={isLight}>Navigatie</NavSectionLabel>
          <NavItem to="/Whenconnect/dashboard" $active={isActive('/Whenconnect/dashboard')} $collapsed={collapsed} $light={isLight}>
            <LayoutDashboard size={17} />
            <NavItemLabel $collapsed={collapsed}>Dashboard</NavItemLabel>
          </NavItem>
          <NavItem to="/Whenconnect/contactinzendingen" $active={isActive('/Whenconnect/contactinzendingen')} $collapsed={collapsed} $light={isLight}>
            <Mail size={17} />
            <NavItemLabel $collapsed={collapsed}>Contactinzendingen</NavItemLabel>
          </NavItem>
          <NavItem to="/Whenconnect/ervaringen" $active={isActive('/Whenconnect/ervaringen')} $collapsed={collapsed} $light={isLight}>
            <Star size={17} />
            <NavItemLabel $collapsed={collapsed}>Ervaringen</NavItemLabel>
          </NavItem>
        </NavSection>

        <NavSection>
          <NavSectionLabel $collapsed={collapsed} $light={isLight}>Extern</NavSectionLabel>
          <ExternalBtn $collapsed={collapsed} $light={isLight} onClick={() => setShowWebsiteConfirm(true)}>
            <ExternalLink size={17} />
            <NavItemLabel $collapsed={collapsed}>Website</NavItemLabel>
          </ExternalBtn>
        </NavSection>

        <AppInfo $collapsed={collapsed}>
          <AppInfoName $light={isLight}>Whenconnect</AppInfoName>
          <AppInfoMeta $light={isLight}>© {new Date().getFullYear()} Webvonk</AppInfoMeta>
        </AppInfo>

        <CollapseBtn $collapsed={collapsed} $light={isLight} onClick={toggleCollapsed}>
          {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
        </CollapseBtn>
      </SideNav>

      {/* Main */}
      <MainArea>
        <TopBar $light={isLight}>
          <div ref={profileRef}>
            <ProfileBtn $light={isLight} onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setProfilePos({ top: r.bottom + 8, right: window.innerWidth - r.right });
              setShowProfile(p => !p);
            }}>
              <Avatar>{initials}</Avatar>
              <ProfileName $light={isLight}>{username}</ProfileName>
              <ChevronDown size={14} style={{ opacity: 0.5, transition: 'transform 0.2s', transform: showProfile ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </ProfileBtn>
          </div>
        </TopBar>

        <ContentArea>{children}</ContentArea>
      </MainArea>

      {/* Profile dropdown */}
      {showProfile && ReactDOM.createPortal(
        <Dropdown $light={isLight} style={{ top: profilePos.top, right: profilePos.right }}>
          <DropdownHeader $light={isLight}>{username}</DropdownHeader>
          <DropdownDivider $light={isLight} />
          {(() => { try { return JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return null; } })() && (
            <DropdownItem $light={isLight} onClick={() => { setShowProfile(false); window.location.href = '/#/company-select'; }}>
              <LayoutGrid size={15} /> Wissel bedrijf
            </DropdownItem>
          )}
          <DropdownItem $light={isLight} $danger onClick={() => { setShowProfile(false); setShowLogout(true); }}>
            <LogOutIcon size={15} /> Uitloggen
          </DropdownItem>
        </Dropdown>,
        document.body
      )}

      {/* Website confirm modal */}
      {showWebsiteConfirm && ReactDOM.createPortal(
        <Overlay onClick={() => setShowWebsiteConfirm(false)}>
          <Modal $light={isLight} onClick={e => e.stopPropagation()}>
            <ModalTitle $light={isLight}>Naar whenconnect.nl</ModalTitle>
            <ModalText $light={isLight}>Weet je zeker dat je naar whenconnect.nl wilt gaan?</ModalText>
            <ModalBtns>
              <ModalBtn $light={isLight} onClick={() => setShowWebsiteConfirm(false)}>Annuleren</ModalBtn>
              <ModalBtn $light={isLight} onClick={() => { setShowWebsiteConfirm(false); window.open('https://whenconnect.nl', '_blank', 'noopener,noreferrer'); }}>Ja, open website</ModalBtn>
            </ModalBtns>
          </Modal>
        </Overlay>,
        document.body
      )}

      {/* Logout modal */}
      {showLogout && ReactDOM.createPortal(
        <Overlay onClick={() => setShowLogout(false)}>
          <Modal $light={isLight} onClick={e => e.stopPropagation()}>
            <ModalTitle $light={isLight}>Uitloggen</ModalTitle>
            <ModalText $light={isLight}>Weet je zeker dat je wilt uitloggen?</ModalText>
            <ModalBtns>
              <ModalBtn $light={isLight} onClick={() => setShowLogout(false)}>Annuleren</ModalBtn>
              <ModalBtn $danger onClick={() => { setShowLogout(false); clearWhenconnectSession(); onLogout(); }}>Uitloggen</ModalBtn>
            </ModalBtns>
          </Modal>
        </Overlay>,
        document.body
      )}
    </AppShell>
  );
}
